import React, { useMemo, Suspense, useRef } from 'react'
import { useTexture } from '@react-three/drei'
import { useDesignStore } from '../../store/designStore'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

const CylindricalBackgroundInner: React.FC = () => {
  const { canvasSettings } = useDesignStore()
  const { camera } = useThree()
  const cylinderRef = useRef<THREE.Mesh>(null)
  
  if (!canvasSettings.backgroundImage) {
    return null
  }

  const texture = useTexture(canvasSettings.backgroundImage, (texture) => {
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.flipY = false
  })

  // Calculate cylinder dimensions and transformations
  const cylinderRadius = useMemo(() => {
    const baseRadius = Math.max(canvasSettings.width, canvasSettings.height) * 1.5
    return baseRadius * (canvasSettings.backgroundImageScale || 1.0)
  }, [canvasSettings.width, canvasSettings.height, canvasSettings.backgroundImageScale])

  const cylinderHeight = useMemo(() => {
    return cylinderRadius * 1.5 // Make it tall enough to surround the scene
  }, [cylinderRadius])

  const cylinderPosition = useMemo(() => {
    return [
      canvasSettings.backgroundImageOffsetX || 0,
      canvasSettings.backgroundImageOffsetY || 0,
      canvasSettings.backgroundImageOffsetZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageOffsetX, canvasSettings.backgroundImageOffsetY, canvasSettings.backgroundImageOffsetZ])

  const cylinderRotation = useMemo(() => {
    return [
      canvasSettings.backgroundImageRotationX || 0,
      canvasSettings.backgroundImageRotationY || 0,
      canvasSettings.backgroundImageRotationZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageRotationX, canvasSettings.backgroundImageRotationY, canvasSettings.backgroundImageRotationZ])

  // Update cylinder position to follow camera
  useFrame(() => {
    if (cylinderRef.current && camera) {
      // Make the panorama follow the camera position but maintain relative offset
      const cameraPosition = camera.position
      cylinderRef.current.position.set(
        cameraPosition.x + (canvasSettings.backgroundImageOffsetX || 0),
        cameraPosition.y + (canvasSettings.backgroundImageOffsetY || 0),
        cameraPosition.z + (canvasSettings.backgroundImageOffsetZ || 0)
      )
    }
  })

  return (
    <mesh
      ref={cylinderRef}
      rotation={cylinderRotation}
    >
      {/* Use cylinder geometry for 360-degree horizontal panorama */}
      <cylinderGeometry args={[cylinderRadius, cylinderRadius, cylinderHeight, 64, 1, true]} />
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={canvasSettings.backgroundImageOpacity || 0.7}
        side={THREE.BackSide} // Render on the inside of the cylinder
        depthWrite={false}
        depthTest={false}
      />
    </mesh>
  )
}

const CylindricalBackground: React.FC = () => {
  const { canvasSettings } = useDesignStore()
  
  if (!canvasSettings.backgroundImage) {
    return null
  }

  return (
    <Suspense fallback={null}>
      <CylindricalBackgroundInner />
    </Suspense>
  )
}

export default CylindricalBackground
